import { FunctionComponent } from 'react';

import { Stack, Typography } from '@mui/material';

import { HStack } from '@dw/components/core';
import { DANCE_SCHOOL_APP_APPEARANCE } from '@dw/models/danceSchool';

import { APP_PALETTE_DARK, APP_PALETTE_LIGHT, AppScreenPreview, PhoneFrame } from '../illustrations';

interface IAppearancePreviewProps {
  appearance: DANCE_SCHOOL_APP_APPEARANCE;
  schoolName: string;
  imageUrl?: string;
  isWide?: boolean;
}

/**
 * Both grounds, side by side, with the answer lit.
 *
 * Two phones rather than one, because the choice being made is a comparison — a school is here
 * precisely because they suspect their logo or their notice colours work on one ground and not the
 * other, and a preview that shows only the option currently selected cannot settle that.
 *
 * "Each dancer decides" lights both, which is not a cosmetic flourish: it is the most literal
 * possible statement of what that option means. Unlit phones are dimmed as a whole rather than
 * greyed out, so the theme they are demonstrating stays truthful even while it is not the answer.
 */
export const AppearancePreview: FunctionComponent<IAppearancePreviewProps> = (props: IAppearancePreviewProps) => {

  const { appearance, schoolName, imageUrl, isWide } = props;

  const grounds = [
    { key: 'light', label: 'Light', palette: APP_PALETTE_LIGHT, pinned: DANCE_SCHOOL_APP_APPEARANCE.LIGHT },
    { key: 'dark', label: 'Dark', palette: APP_PALETTE_DARK, pinned: DANCE_SCHOOL_APP_APPEARANCE.DARK }
  ];

  return (
    <HStack
      breakpoint={false}
      columnGap={2.5}
      alignItems='flex-start'
      width='100%'
      /*
        `safe center` rather than `center`: once the pair is wider than the card it has to scroll,
        and plain centring pushes the first phone off the scrollable start where it cannot be
        reached at all. Safe alignment falls back to flex-start exactly when that would happen.
      */
      sx={{ overflowX: 'auto', justifyContent: 'safe center' }}
    >
      {grounds.map(ground => {
        const isLit = appearance === DANCE_SCHOOL_APP_APPEARANCE.DANCER_CHOICE || appearance === ground.pinned;

        return (
          <Stack key={ground.key} rowGap={1} alignItems='center'>
            <PhoneFrame palette={ground.palette} dimmed={!isLit} width={160}>
              <AppScreenPreview
                palette={ground.palette}
                schoolName={schoolName}
                imageUrl={imageUrl}
                isWide={isWide}
              />
            </PhoneFrame>

            <Typography
              variant='caption'
              fontWeight={isLit ? 600 : 400}
              color={isLit ? 'text.primary' : 'text.disabled'}
            >
              {ground.label}
            </Typography>
          </Stack>
        );
      })}
    </HStack>
  );
};
